import { isPlatformServer } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  Inject,
  inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  Signal,
  ViewChild,
  WritableSignal,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatFabButton } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { CustomerModel } from '@libs/account/models';
import { CmsComponent } from '@libs/cms/components';
import { ContentConfig } from '@libs/cms/models';
import { AppStoreBannerComponent, DesktopNavigationComponent, SideSheetComponent } from '@libs/common/components';
import { BlockedCustomerInfoComponent, FooterComponent, HeaderComponent, NavbarComponent } from '@libs/components/cep';
import { IconComponent } from '@libs/components/icons';
import { AuthService, CustomerUpdateService } from '@libs/shared/auth';
import { AppStateService, CapacitorService, CepStateService, DialogService } from '@libs/shared/common';
import { CapabilityService } from '@libs/shared/services';
import { LiveUpdateService } from '@libs/shared/live-update';
import { CHECKOUT_CART_ROUTE_PATH, CHECKOUT_OVERVIEW_ROUTE_PATH, navbarOverlayRoutes, SCANNER_ROUTE_NAME } from '@libs/shared/models';
import {
  AppRedirectService,
  CartPollingService,
  CartScannerHandlerService,
  PageViewService,
  ScrollService,
  ViewportService,
  VirtualKeyboardService,
} from '@libs/shared/services';
import { SearchSuggestionsListComponent } from '@libs/shop/components';
import { debounceTime, delay, filter, first, firstValueFrom } from 'rxjs';
import { AgentStatusBarComponent } from '../agent';

@Component({
  selector: 'ui-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NavbarComponent,
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    MatDialogModule,
    DesktopNavigationComponent,
    MatFabButton,
    CmsComponent,
    IconComponent,
    BlockedCustomerInfoComponent,
    SideSheetComponent,
    AgentStatusBarComponent,
    AppStoreBannerComponent,
  ],
})
export class ShellComponent implements OnInit, OnDestroy {
  @ViewChild('wrapperContent') public wrapperContentElRef: ElementRef | undefined;

  public readonly capabilityService: CapabilityService = inject(CapabilityService);

  public authorized: Signal<boolean> = toSignal(this.authService.isUserAuthorized$('user'), { initialValue: false });
  public isDesktopXl: Signal<boolean> = toSignal(this.viewportService.isDesktopXl(), { initialValue: false });

  public searchTextValue: Signal<string> = computed(() => this.cepStateService.searchParams().q ?? '');

  public isOverlayRoute: WritableSignal<boolean> = signal(false);
  public isFullscreenRoute: WritableSignal<boolean> = signal(false);

  public isCapacitorApp: WritableSignal<boolean> = signal(Capacitor.getPlatform() !== 'web');
  public customer: Signal<CustomerModel | undefined> = this.authService.currentCustomer;
  public isCurrentCustomerBlocked: Signal<boolean> = this.authService.isCurrentCustomerBlocked;

  public showInfobar: WritableSignal<boolean> = signal(true);
  public infobarConfig: Signal<ContentConfig | undefined> = computed(() => {
    return (this.authorized() || this.capabilityService.isAvailable('platform.publicApp')()) && this.showInfobar()
      ? { contentId: '2428', customerNumber: this.customer()?.id }
      : undefined;
  });

  public hideScannerFABOnRoutes: string[] = [CHECKOUT_CART_ROUTE_PATH, CHECKOUT_OVERVIEW_ROUTE_PATH];

  public isScannerFABVisible: Signal<boolean> = computed(() => {
    return this.isCapacitorApp() &&
      this.authorized() &&
      !!this.customer() &&
      !this.hideScannerFABOnRoute();
  });

  private readonly hideScannerFABOnRoute: WritableSignal<boolean> = signal(true);

  public constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly dialog: DialogService,
    private readonly viewportService: ViewportService,
    private readonly scrollService: ScrollService,
    private readonly virtualKeyboardService: VirtualKeyboardService,
    private readonly capacitorService: CapacitorService,
    private readonly cartPollingService: CartPollingService,
    private readonly destroyRef: DestroyRef,
    private readonly appStateService: AppStateService,
    private readonly pageViewService: PageViewService,
    public readonly cepStateService: CepStateService,
    public readonly customerUpdateService: CustomerUpdateService,
    private readonly appRedirectService: AppRedirectService,
    public liveUpdateService: LiveUpdateService,
    @Inject(PLATFORM_ID) public readonly platformId: string,
  ) {

    this.scrollService.getContentScrollToTopEvent().pipe(
      // minimal delay needed for smooth scrolling to work in Firefox
      delay(100),
      takeUntilDestroyed(),
    ).subscribe(anchor => {
      this.scrollToTop(anchor);
    });

    this.hideScannerFABOnRoute.set(this.hideScannerFABOnRoutes.some(x => this.router.url.slice(1).includes(x)));
    this.router.events.pipe(
      takeUntilDestroyed(),
      filter(event => event instanceof NavigationEnd),
      // debounceTime to prevent multiple rapid navigation events
      debounceTime(100),
    ).subscribe((event) => {
      this.onNavigationEnd(event);
    });

    void this.capacitorService.initNavigationOnAndroidBackButton(this.destroyRef);
    void this.capacitorService.initNativeLifecycleEvents(this.destroyRef);
    this.capacitorService.initNativeUpdateCheck(this.destroyRef);
    void this.appRedirectService.init(this.destroyRef);

    effect(() => {
      if (this.authorized()) {
        this.cartPollingService.init();
        this.customerUpdateService.initCustomerUpdateOnResume();
      } else {
        this.cartPollingService.dispose();
        this.customerUpdateService.removeCustomerUpdateOnResume();
      }
    });

    effect(() => {
      this.cepStateService.setHideScannerFAB(!this.isScannerFABVisible());
    });
  }

  public ngOnDestroy(): void {
    this.cartPollingService.dispose();
    this.customerUpdateService.removeCustomerUpdateOnResume();
  }

  public ngOnInit(): void {
    void this.loginCustomerAndShop();
  }

  public openBarcodeScanner(): Promise<boolean> {
    return this.router.navigate([SCANNER_ROUTE_NAME, CartScannerHandlerService.Key, '']);
  }

  public openMobileSearchOverlay(): void {
    const dialogRef: MatDialogRef<SearchSuggestionsListComponent> = this.dialog.open(SearchSuggestionsListComponent, {
      height: '100vh',
      width: '100vw',
      maxWidth: '100vw',
      autoFocus: false,
      panelClass: 'search-suggestion-list',
    });

    // sometimes the dialog does not close -> force it to close
    dialogRef.afterClosed().pipe(first()).subscribe(() => {
      this.dialog.closeAll();
    });
  }

  public onUserLogin(): void {
    void firstValueFrom(this.authService.login());
  }

  public async loginCustomerAndShop(): Promise<void> {
    await this.authService.checkAuthState();
    setTimeout(() => {
      void this.liveUpdateService.navigateToPreviousUrl();
    });
  }

  public onNavigationEnd(event: NavigationEnd): void {
    this.scrollToTop();
    void this.virtualKeyboardService.hide();

    const currentRoute: string = event.url.slice(1);

    this.hideScannerFABOnRoute.set(this.hideScannerFABOnRoutes.some(x => currentRoute.includes(x)));
    this.showInfobar.set(!currentRoute.includes(CHECKOUT_OVERVIEW_ROUTE_PATH));
    this.isOverlayRoute.set(navbarOverlayRoutes.some(x => currentRoute.includes(x)));
    this.isFullscreenRoute.set(currentRoute.includes(SCANNER_ROUTE_NAME));

    if (this.isOverlayRoute() || this.isFullscreenRoute()) {
      this.hideScannerFABOnRoute.set(true);
    }

    void firstValueFrom(this.pageViewService.getCurrentNode())
      .then(node => {
        if (node) {
          this.appStateService.set('lastContentUrl', this.router.url);
        }
      });
  }

  public scrollToTop(anchor?: HTMLElement): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    const container: HTMLElement = this.wrapperContentElRef?.nativeElement as HTMLElement;

    if (!anchor) {
      container.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

      return;
    }

    const containerRect: DOMRect = container.getBoundingClientRect();
    const anchorRect: DOMRect = anchor.getBoundingClientRect();

    // We need to position the anchor relative to the content wrapper (container),
    // therefore compute Δy relative to the container element. We subtract a
    // 130px constant as a safety offset to make sure the anchor element is fully
    // visible (e.g., not hidden behind sticky headers). Calling scrollIntoView on
    // the anchor element itself did not work reliably across targets, so we
    // perform a relative scroll on the container instead.
    const top: number = anchorRect.top - containerRect.top - 130;
    const left: number = 0;

    container.scrollBy({ top, left, behavior: 'smooth' });
  }
}
